import {
  SHBox,
  SHIconLoading,
  SHStack,
  SHTextField,
} from "@components/design-systems";
import { Controller, useFormContext } from "react-hook-form";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { CheckSVG, CloseSVG } from "@components/svgs";
import { useEffect, useMemo, useState } from "react";
import { useIsNew } from "@hooks/useIsNew";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { useUserPermissions } from "@hooks/userUserPermission";
import { debounce, isEmpty } from "lodash";
import { AnalysisSetupStepConstants } from "@pages/platform-analysis-v2/_id/steps/setup/constants";

export const AnalysisSetupNameField = ({
  userType,
  disabled,
  isSupplierUser = userType === "supplier",
}: AnalysisSetupStepRenderedFieldProps) => {
  const isNew = useIsNew();
  const { user, isSupplierGroup } = useUserPermissions();
  const { platformAnalysis } = useSelector(
    (state: RootState) => state.platformAnalysis,
  );
  const {
    control,
    watch,
    getValues,
    formState: { errors, isValid, isDirty, dirtyFields },
  } = useFormContext<AnalysisSetupDTO>();
  const { checkPlatformAnalysisUniqueName, setIsValid, setIsDirty } =
    usePlatformAnalysis();
  setIsDirty(isDirty && !isEmpty(dirtyFields));

  const watchedOwnerId = watch("ownerId");

  const [verifyAnalysisName, setVerifyAnalysisName] = useState<
    "error" | "success" | "checking" | "none"
  >(isNew ? "none" : "success");
  const [verifyAnalysisNameErrorMessage, setVerifyAnalysisNameErrorMessage] =
    useState("");

  const handleOnChecking = (name: string | null) => {
    const ownerId = getValues("ownerId");
    const firmId = isSupplierGroup
      ? user?.userMetadata?.supplier_id
      : user?.userMetadata?.adviser_firm_id;

    if (!name || name.trim().length === 0 || !firmId || !ownerId) {
      setVerifyAnalysisName("none");
      return;
    }

    const { name: platformAnalysisName, ownerId: platformAnalysisOwnerId } =
      platformAnalysis?.setup || {};
    if (platformAnalysisName === name && platformAnalysisOwnerId === ownerId) {
      setVerifyAnalysisName("success");
      return;
    }

    setVerifyAnalysisName("checking");
    debounceHandleOnChecking(name, firmId, ownerId);
  };

  const debounceHandleOnChecking = useMemo(
    () =>
      debounce((name: string, firmId: string, ownerId: string) => {
        checkPlatformAnalysisUniqueName({
          firmId,
          name,
          ownerId,
        }).then(({ isSuccess, message }) => {
          if (isSuccess) {
            setVerifyAnalysisName("success");
          } else {
            setVerifyAnalysisName("error");
            setVerifyAnalysisNameErrorMessage(message || "");
          }
        });
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const renderVerifyAnalysisName = () => {
    switch (verifyAnalysisName) {
      case "success":
        return <CheckSVG width={18} height={18} />;
      case "error":
        return (
          <SHBox px={"4px"} py={"1px"}>
            <CloseSVG width={10} height={10} />
          </SHBox>
        );
      case "checking":
        return <SHIconLoading size={16} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    const currentName = getValues("name");
    handleOnChecking(currentName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedOwnerId]);

  useEffect(() => {
    setIsValid(isValid && verifyAnalysisName === "success");
    // eslint-disable-next-line
  }, [isValid, verifyAnalysisName]);

  useEffect(() => {
    return () => {
      debounceHandleOnChecking.cancel();
    };
  }, [debounceHandleOnChecking]);

  return (
    <Controller
      name="name"
      control={control}
      render={({ field }) => (
        <SHTextField
          {...field}
          hasCounter
          maxLength={AnalysisSetupStepConstants.MaxAnalysisNameLength}
          required
          sx={{ width: "100%" }}
          label={`Name of ${isSupplierUser ? "analysis" : "review"}`}
          placeholder={"Enter a unique name"}
          disabled={disabled}
          error={!!errors.name}
          helperText={
            errors?.name?.message ??
            (verifyAnalysisName === "error"
              ? verifyAnalysisNameErrorMessage
              : null)
          }
          onChange={(event) => {
            const name = event.target.value;
            field.onChange(name);
            debounceHandleOnChecking.cancel();
            setTimeout(() => handleOnChecking(name), 0);
          }}
          InputProps={{
            sx: {
              "&>input": {
                pr: "32px !important",
              },
            },
            endAdornment: (
              <SHStack
                sx={{
                  position: "absolute",
                  right: "8px",
                }}
              >
                {renderVerifyAnalysisName()}
              </SHStack>
            ),
          }}
        />
      )}
    />
  );
};
